import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup } from '@/components/ui/radio-group';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { logUiEvent } from '@/utils/logger';
import { useAuth } from '@/contexts/auth';
import AssistantCard from './AssistantCard';
import AssistantLoadingSkeleton from './AssistantLoadingSkeleton';
import AssistantError from './AssistantError';
import AssistantEmpty from './AssistantEmpty';
import NotAuthenticatedState from './NotAuthenticatedState';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';

export const AssistantSelection: React.FC = () => {
  const { user } = useAuth(); // Now uses centralized auth context
  
  const { 
    assistants, 
    activeAssistantId, 
    setActiveAssistantId, 
    isLoadingAssistants: isLoading, 
    assistantsError: error,
    assistantsLoaded: isInitialized 
  } = useAIAgentChat();

  // Log component render (only when relevant state changes)
  React.useEffect(() => {
    if (isInitialized) {
      console.log("=== ASSISTANT SELECTION COMPONENT (CENTRALIZED AUTH) ===");
      console.log("AssistantSelection component rendered:", {
        hasUser: !!user,
        userId: user?.id,
        assistantCount: assistants?.length || 0,
        activeAssistantId,
        isLoading,
        hasError: !!error,
        isInitialized
      });
      
      logUiEvent('AssistantSelection', 'component rendered (centralized auth)', {
        assistantCount: assistants?.length || 0,
        activeAssistant: activeAssistantId,
        isLoading,
        hasError: !!error,
        isAuthenticated: !!user
      });

      console.log("=== END ASSISTANT SELECTION COMPONENT (CENTRALIZED AUTH) ===");
    }
  }, [assistants?.length, activeAssistantId, isLoading, error, user, isInitialized]);

  // Handle assistant selection
  const handleAssistantChange = React.useCallback((assistantId: string) => {
    logUiEvent('AssistantSelection', 'assistant selected (centralized auth)', { 
      previousId: activeAssistantId, 
      newId: assistantId 
    });
    console.log("Assistant selected (centralized auth flow):", assistantId);
    setActiveAssistantId(assistantId);
  }, [activeAssistantId, setActiveAssistantId]);

  // Handle retry when there's an error
  const handleRetry = React.useCallback(() => {
    logUiEvent('AssistantSelection', 'retry clicked (centralized auth)');
    console.log("Retry loading assistants (centralized auth)");
    window.location.reload(); // Simple refresh for now
  }, []);

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardContent className="p-0">
        {!user ? (
          <NotAuthenticatedState />
        ) : isLoading ? (
          <AssistantLoadingSkeleton />
        ) : error ? (
          <AssistantError error={error} onRetry={handleRetry} />
        ) : !assistants || assistants.length === 0 ? (
          <AssistantEmpty onRetry={handleRetry} />
        ) : (
          <RadioGroup 
            value={activeAssistantId || undefined}
            onValueChange={handleAssistantChange}
            className={`grid ${GRID_BREAKPOINTS.imageCards} gap-6`}
          >
            {assistants.map((assistant) => (
              <AssistantCard 
                key={assistant.id}
                assistant={assistant}
                isActive={activeAssistantId === assistant.id}
                onClick={handleAssistantChange}
              />
            ))}
          </RadioGroup>
        )}
      </CardContent>
    </Card>
  );
};

export default AssistantSelection;
